import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { IconCheck, IconMinus } from "@tabler/icons-react";
import { cva } from "class-variance-authority";
import * as React from "react";

import { focusStyles } from "@beweegbaas/ui/lib/focus-styles";
import { cn } from "@beweegbaas/ui/lib/utils";

/** `skipped`: passed without anything in it. */
type StepperItemState = "current" | "done" | "skipped" | "upcoming";

const StepperItemContext = React.createContext<{
  state: StepperItemState;
  descriptionId: string;
} | null>(null);

function useStepperItem(part: string) {
  const context = React.use(StepperItemContext);
  if (!context) throw new Error(`<${part}> belongs inside a <StepperItem>`);
  return context;
}

/**
 * Steps in their order, every one of them still clickable. Below `md` the circles stand evenly
 * apart from edge to edge with the labels under them; from `md` on each label sits next to its
 * circle, with equally long lines between the steps.
 */
function Stepper({ className, children, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav data-slot="stepper" className={className} {...props}>
      <ol className="m-0 flex list-none items-start p-0 md:gap-3">{children}</ol>
    </nav>
  );
}

function StepperItem({
  state,
  className,
  children,
  ...props
}: React.ComponentProps<"li"> & { state: StepperItemState }) {
  const descriptionId = React.useId();
  const context = React.useMemo(() => ({ state, descriptionId }), [state, descriptionId]);
  return (
    <StepperItemContext value={context}>
      <li
        data-slot="stepper-item"
        data-state={state}
        // The steps share the room that is left, which their lines take: on a phone, where a step
        // is as wide as its circle, that puts the circles evenly apart.
        className={cn(
          "group/stepper-item flex flex-auto items-start last:flex-none md:gap-3",
          className,
        )}
        {...props}
      >
        {/* As wide as the circle on a phone, so a label sticks out on both sides of it; the
            first and last line up with the edges instead. */}
        <div
          className="flex w-6 shrink-0 flex-col items-center gap-px
            max-md:group-first/stepper-item:items-start max-md:group-last/stepper-item:items-end
            md:w-auto md:items-start md:gap-0"
        >
          {children}
        </div>
        <span
          aria-hidden
          data-slot="stepper-separator"
          className="bg-border mt-[11.25px] h-[1.5px] flex-1 group-last/stepper-item:hidden md:min-w-6"
        />
      </li>
    </StepperItemContext>
  );
}

/** The step's circle and title, to go to it: a link, or anything else through `render`. */
function StepperTrigger({ className, render, ...props }: useRender.ComponentProps<"a">) {
  const { state, descriptionId } = useStepperItem("StepperTrigger");
  return useRender({
    defaultTagName: "a",
    render,
    props: mergeProps<"a">(
      {
        className: cn(
          "group/stepper-trigger flex cursor-pointer flex-col items-center gap-1.5 rounded-md no-underline",
          "max-md:group-first/stepper-item:items-start max-md:group-last/stepper-item:items-end",
          "md:flex-row md:items-start md:gap-2.5",
          "aria-disabled:pointer-events-none aria-disabled:opacity-50",
          focusStyles,
          className,
        ),
      },
      props,
      // Last, so a router link's own aria-current="page" gives way.
      {
        "aria-current": state === "current" ? "step" : undefined,
        "aria-describedby": descriptionId,
      },
    ),
    state: { slot: "stepper-trigger", state },
  });
}

const indicatorVariants = cva(
  "flex size-6 shrink-0 items-center justify-center rounded-full text-xs leading-4 tabular-nums transition-colors",
  {
    variants: {
      state: {
        current: "bg-primary text-primary-foreground font-bold",
        done: "bg-(--tint-green-bg) text-(--tint-green-fg)",
        skipped: "bg-muted text-muted-foreground",
        upcoming: [
          "bg-background text-muted-foreground border-foreground/15 border-[1.5px] font-semibold",
          "group-hover/stepper-trigger:border-foreground/30",
        ],
      },
    },
  },
);

/** The circle: the step's number (its children) until it is done or skipped. */
function StepperIndicator({ className, children, ...props }: React.ComponentProps<"span">) {
  const { state } = useStepperItem("StepperIndicator");
  return (
    <span
      aria-hidden
      data-slot="stepper-indicator"
      className={cn(indicatorVariants({ state }), className)}
      {...props}
    >
      {state === "done" ? (
        <IconCheck className="size-3.5" stroke={3} />
      ) : state === "skipped" ? (
        <IconMinus className="size-3.5" stroke={3} />
      ) : (
        children
      )}
    </span>
  );
}

function StepperTitle({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="stepper-title"
      className={cn(
        "text-muted-foreground text-xs leading-4 font-semibold whitespace-nowrap md:text-sm md:leading-6",
        "group-data-[state=current]/stepper-item:text-foreground group-data-[state=done]/stepper-item:text-foreground",
        "group-hover/stepper-trigger:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

/**
 * What the step holds, under its title; it describes the step's trigger. Kept when empty, so the
 * steps stay as high as each other.
 */
function StepperDescription({ className, ...props }: React.ComponentProps<"div">) {
  const { descriptionId } = useStepperItem("StepperDescription");
  return (
    <div
      id={descriptionId}
      data-slot="stepper-description"
      className={cn(
        "text-muted-foreground flex min-h-3.5 items-center text-[11px] leading-3.5 whitespace-nowrap",
        "md:min-h-4 md:pl-8.5 md:text-xs md:leading-4",
        className,
      )}
      {...props}
    />
  );
}

export {
  Stepper,
  StepperDescription,
  StepperIndicator,
  StepperItem,
  StepperTitle,
  StepperTrigger,
  type StepperItemState,
};
